import {Component, inject, signal, DestroyRef, effect, OnInit, ChangeDetectionStrategy} from '@angular/core';

import {FormBuilder, FormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {EditorModule, TINYMCE_SCRIPT_SRC} from '@tinymce/tinymce-angular';
import {ContractRestService} from '../../core/api/contract-rest.service';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {Template} from '../../core/models/template.model';
import {from} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ThemeService} from '../../core/services/theme.service';
import {MatSelectModule} from '@angular/material/select';
import { PageComponent } from './page/page.component';
import { ContractService } from '../../core/services/contract.service';


@Component({
  selector: 'app-new-template',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    EditorModule,
    MatSnackBarModule,
    MatSelectModule,
    PageComponent
  ],
  providers: [
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: '/tinymce/tinymce.min.js'
    }
  ],
  templateUrl: './new-template.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./new-template.component.scss']
})
export class NewTemplateComponent implements OnInit{
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private contractRestService: ContractRestService = inject(ContractRestService);
  protected contractService: ContractService = inject(ContractService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private themeService: ThemeService = inject(ThemeService);
  public template: Template | null = null;
  companies: any = [
    {name: 'Phoenix', value: 'PHOENIX'},
    {name: 'Hageda', value: 'HAGEDA'}
  ]

  isSubmitting = signal(false);
  isTemplateSaved = signal(false);

  templateForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    shortDescription: ['', [Validators.required]],
    company: ['', [Validators.required]],
  });


  editorConfig: any;
  showEditor = signal(true);
  pages: any[] = [];

  currentTemplate: any = null;
  editTemplate = false;


  //
  //  *****  local config  *****
  //
  // editorConfig = {
  //   skin: 'light',
  //   base_url: '/tinymce',
  //   suffix: '.min',
  //   height: 500,
  //   promotion: false,
  //   branding: false,
  //   menubar: true,
  //   plugins: [
  //     'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
  //     'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
  //     'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
  //   ],
  //   toolbar: 'undo redo | blocks | ' +
  //     'bold italic forecolor | alignleft aligncenter ' +
  //     'alignright alignjustify | bullist numlist outdent indent | ' +
  //     'removeformat | help',
  //   content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
  //   language: 'de'
  // };

  constructor() {
    this.updateEditorConfig();

    effect(() => {
      const isDark = this.themeService.isDarkMode();
      this.showEditor.set(false);
      this.updateEditorConfig();
    });
  }

  ngOnInit() {
    if(this.contractService.currentTemplate() !== 0) {
      from(this.contractRestService.getTemplateById(this.contractService.currentTemplate()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next:(response: Template) => {
          const data = {name: response.name, company: response.company, shortDescription: response.description};
          this.templateForm.patchValue(data);
          this.template = response;
        },
        error: (error: any) => {
          console.error('❌ Error getting template:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error body:', error.error);

          this.isSubmitting.set(false);

          let errorMessage = 'Fehler beim loading der Vorlage';

          if (error.status === 401) {
            errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
          } else if (error.status === 403) {
            errorMessage = 'Keine Berechtigung zum loading von Vorlagen.';
          } else if (error.status === 400) {
            errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
          } else if (error.status === 0) {
            errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
    }
    this.editTemplate = this.contractService.editTemplate();
  }

  onTemplateSubmit(): void {
    if (!this.templateForm.valid) {
      Object.keys(this.templateForm.controls).forEach(key => {
        this.templateForm.get(key)?.markAsTouched();
      });

      this.snackBar.open('Bitte füllen Sie alle Pflichtfelder aus', 'Schließen', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['warning-snackbar']
      });

      return;
    }

    this.isSubmitting.set(true);

    let templateData: any = {
      name: this.templateForm.value.name,
      description: this.templateForm.value.shortDescription,
      pages: this.pages,
      company: this.templateForm.value.company
    };

    if (this.template != null) {
      templateData['id'] = this.template.id;
      this.updateTemplate(templateData);
    } else {
      this.createTemplate(templateData);
    }
  }

  createTemplate(data: any) {
    console.log('📤 Sending template data:', data);

    from(this.contractRestService.createTemplate(data))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: Template) => {
          console.log('✅ Template created:', response);
          this.isSubmitting.set(false);

          this.snackBar.open('Vorlage erfolgreich erstellt!', 'Schließen', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          });

          this.template = response;
          this.contractService.editTemplate.set(false);

          this.pages = response?.pages || [];

          console.log('📄 Pages after create:', this.pages);
          this.isTemplateSaved.set(true);
          this.contractService.currentTemplate.set(response.id);
        },
        error: (error: any) => {
          console.error('❌ Error creating template:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error body:', error.error);

          this.isSubmitting.set(false);

          let errorMessage = 'Fehler beim Erstellen der Vorlage';

          if (error.status === 401) {
            errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
          } else if (error.status === 403) {
            errorMessage = 'Keine Berechtigung zum Erstellen von Vorlagen.';
          } else if (error.status === 400) {
            errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
          } else if (error.status === 0) {
            errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  updateTemplate(data: any) {
    from(this.contractRestService.updateTemplate(data))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: Template) => {
          console.log('✅ Template updated:', response);
          this.isSubmitting.set(false);

          this.snackBar.open('Vorlage erfolgreich aktualisiert!', 'Schließen', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          });

          this.template = response;
          // ✅ Safe access to pages
          this.pages = response?.pages || [];

          console.log('📄 Pages after update:', this.pages);
          this.isTemplateSaved.set(true);
          this.contractService.editTemplate.set(false);
        },
        error: (error: any) => {
          console.error('❌ Error updating template:', error);
          this.isSubmitting.set(false);

          let errorMessage = 'Fehler beim Aktualisieren der Vorlage';

          if (error.status === 401) {
            errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
          } else if (error.status === 403) {
            errorMessage = 'Keine Berechtigung zum Aktualisieren von Vorlagen.';
          } else if (error.status === 400) {
            errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
          } else if (error.status === 0) {
            errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
          }

          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  onCancel(): void {
    this.router.navigate(['/dashboard']);
  }

  private updateEditorConfig() {
    const isDark = this.themeService.isDarkMode();
    this.editorConfig = {
      skin: isDark ? 'oxide-dark' : 'oxide',
      content_css: isDark ? 'dark' : 'default',
      height: 500,
      branding: false,
      menubar: true,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'help', 'wordcount'
      ],
      toolbar: 'undo redo | blocks | ' +
        'bold italic forecolor backcolor | alignleft aligncenter ' +
        'alignright alignjustify | bullist numlist outdent indent | ' +
        'removeformat | table link image | code fullscreen | help',
      content_style: isDark
        ? 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; background-color: #1e1e1e; color: #e0e0e0; }'
        : 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }',
      language: 'de',
      link_context_toolbar: true,
      image_advtab: true,
      image_caption: true,
      table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol'
    };
  }

  // addPage() {
  //   if (!this.pageForm.valid) {
  //     this.snackBar.open('Bitte füllen Sie den Seiteninhalt aus', 'Schließen', {
  //       duration: 3000,
  //       horizontalPosition: 'end',
  //       verticalPosition: 'top',
  //       panelClass: ['warning-snackbar']
  //     });
  //     return;
  //   }
  //
  //   console.log('📝 Adding page with content:', this.pageForm.value.content);
  //
  //   const data = {
  //     sort_index: this.pages.length,
  //     body_html: this.pageForm.value.content,
  //     header_html: 'Header',
  //     footer_html: 'Footer',
  //     template_id: this.template?.id,
  //     id: this.pageForm.value.pageId
  //   }
  //
  //   // console.log('📄 Total pages:', this.pages.length);
  //   // this.onSubmit();
  //   this.addNewPage(data)
  // }

}
